import {
  type QueryKey,
  type UndefinedInitialDataOptions,
  type UseSuspenseQueryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  Server,
  ServerForm,
  ServerTestRequest,
} from "@/app/servers/types";
import { toolsKeys } from "@/app/tools/api/query-keys";
import type { ToolResponse } from "@/app/tools/types";
import type { ScopedBinding } from "@/app/types";
import { getEngineApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import { checkHealthQueryOptions } from "./check-health";
import { createServer } from "./create-server";
import { deleteServer } from "./delete-server";
import {
  type GetAuthorizationUrlOptions,
  getAuthorizationUrl,
} from "./get-authorization-url";
import { getDeploymentStatusQueryOptions } from "./get-deployment-status";
import { getServerQueryOptions } from "./get-server";
import { getServerToolsQueryOptions } from "./get-server-tools";
import { serversQueryOptions } from "./get-servers";
import { serversKeys } from "./query-keys";
import { testServer } from "./test-server";
import { updateServer } from "./update-server";

export const useGetServers = <TData = Server[]>(
  binding: ScopedBinding = "project",
  options?: Omit<
    UseSuspenseQueryOptions<Server[], Error, TData, QueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const scope = useResourceScope();

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  return useSuspenseQuery<Server[], Error, TData, QueryKey>({
    ...serversQueryOptions({ binding, scope }),
    ...options,
  } as UseSuspenseQueryOptions<Server[], Error, TData, QueryKey>);
};

export const useGetServer = (
  id: string,
  binding: ScopedBinding = "project"
) => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...getServerQueryOptions({ binding, id, scope }),
  });
};

type GetServerToolsOptions = Omit<
  UndefinedInitialDataOptions<
    ToolResponse,
    Error,
    ToolResponse,
    readonly unknown[]
  >,
  "queryKey" | "queryFn"
> & {
  binding?: ScopedBinding;
};

export const useGetServerTools = (
  id: string,
  options: GetServerToolsOptions = {}
) => {
  const { binding = "project", enabled, ...restOptions } = options;
  const scope = useResourceScope();
  return useQuery({
    ...getServerToolsQueryOptions({ id, binding, scope }),
    enabled: enabled ?? true,
    ...restOptions,
  });
};

export const useCreateServer = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (server: ServerForm) => createServer({ client, server }),
    meta: {
      invalidates: [serversKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to save server - please try again",
    },
    onSuccess: () => {
      toast.success("Server created successfully");
    },
  });
};

export const useUpdateServer = ({
  id,
  binding = "project",
}: {
  id: string;
  binding?: ScopedBinding;
}) => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (server: Partial<ServerForm>) =>
      updateServer({ client, id, server }),
    meta: {
      invalidates: [serversKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to update server - please try again",
    },
    onSuccess: () => {
      toast.success("Server updated successfully");
    },
  });
};

export const useDeleteServer = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (serverId: string) => deleteServer({ client, serverId }),
    meta: {
      invalidates: [serversKeys.lists({ scope }), toolsKeys.lists({ scope })],
      errorMessage: "Unable to delete server - please try again",
    },
    onSuccess: () => {
      toast.success("Server deleted successfully");
    },
  });
};

export const useCheckServerHealth = ({
  id,
  options,
  enabled = true,
  binding = "project",
}: {
  id: string;
  options?: { refetchInterval?: number };
  enabled?: boolean;
  binding?: ScopedBinding;
}) => {
  const scope = useResourceScope();
  return useQuery({
    ...checkHealthQueryOptions({
      serverId: id,
      binding,
      refetchInterval: options?.refetchInterval,
      scope,
    }),
    enabled,
  });
};

export const useTestServer = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (request: ServerTestRequest) => testServer({ client, request }),
  });
};

export const useGetAuthorizationUrl = (binding: ScopedBinding = "project") => {
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  return useMutation({
    mutationFn: (options: Omit<GetAuthorizationUrlOptions, "client">) =>
      getAuthorizationUrl({ ...options, client }),
    meta: {
      errorMessage: "Unable to get authorization URL - please try again",
    },
  });
};

export const useGetDeploymentStatus = ({
  id,
  enabled,
  activeInterval,
  stableInterval,
  binding = "project",
}: {
  id: string;
  enabled: boolean;
  activeInterval: number;
  stableInterval: number;
  binding?: ScopedBinding;
}) => {
  const scope = useResourceScope();

  return useQuery({
    ...getDeploymentStatusQueryOptions({
      serverId: id,
      binding,
      activeInterval,
      stableInterval,
      scope,
    }),
    enabled,
  });
};

/**
 * Hook to check if a server's OAuth authorization is completed.
 * Only queries the authorize endpoint for MCP servers, since HTTP servers
 * do not support OAuth authorization.
 */
export const useServerAuthStatus = (
  serverId: string,
  binding: ScopedBinding = "project",
  serverType?: string
) => {
  const isMcp = serverType === "mcp";
  const scope = useResourceScope();
  const client = getEngineApi(scope, binding);

  const { data: authStatus, isLoading } = useQuery({
    queryKey: serversKeys.authStatus({ serverId, binding, scope }),
    queryFn: () => getAuthorizationUrl({ serverId, client }),
    enabled: !!serverId && isMcp,
    // Short staleTime so data is considered stale quickly. This triggers a refetch
    // on window focus (when user returns from OAuth in another tab), component
    // mount, or network reconnect, not continuous polling.
    staleTime: 1000,
  });

  return {
    isAuthorized: authStatus?.status === "completed",
    isPending: authStatus?.status === "pending",
    isLoading: isMcp ? isLoading : false,
    status: authStatus?.status,
  };
};
